/**
 * Browser fetch over the relay (POST /v1/users/{user_id}/browser/fetch): the server asks the box's
 * runner to open one page in a background tab and keep the bodies of the page's own data calls,
 * with no model; the runner answers once, echoing the requestId (awaitSandboxEvent). Server ↔
 * runner only, never forwarded to a browser.
 *
 * Also the one domain rule both sides apply (the server before it wakes the box, the runner on
 * every page the tab lands on), so they can never disagree about what a deployment allows.
 */
import { z } from 'zod';

/** Every body of one fetch together: past this, later bodies come back cut (the runner applies it). */
export const BROWSER_FETCH_TOTAL_BYTES = 8_000_000;

const pageFetchCall = {
  url: z.string().min(1), method: z.enum(['GET', 'POST']).optional(), headers: z.record(z.string(), z.string()).optional(),
  body: z.string().optional(), capture_as: z.string().min(1), max_bytes: z.number().int().positive(),
};

const FetchStepSchema = z.union([
  z.object({
    op: z.literal('click'), role: z.string().optional(), name: z.string().optional(), text: z.string().optional(), optional: z.boolean().optional(), within_ms: z.number().int().positive().optional(),
    nth: z.number().int().positive().optional(), near_text: z.union([z.string(), z.array(z.string())]).optional(),
  }),
  // `text` here is what to type, never part of a name to look for.
  z.object({ op: z.literal('type'), role: z.string().optional(), name: z.string().optional(), text: z.string().min(1), submit: z.boolean().optional(), optional: z.boolean().optional() }),
  z.object({ op: z.literal('scroll'), times: z.number().int().positive() }),
  z.object({ op: z.literal('wait_for'), match: z.string().min(1) }),
  // As the caller sent it (snake_case), with max_bytes filled in by the server: one call, or a batch of them.
  z.object({ op: z.literal('page_fetch'), ...pageFetchCall }),
  z.object({ op: z.literal('page_fetch'), calls: z.array(z.object(pageFetchCall)).min(1) }),
]);
export type BrowserFetchStepPayload = z.infer<typeof FetchStepSchema>;

/** Server → runner (BROWSER_FETCH_REQUEST): apps/server platform/compute/browser-fetch.ts, after the allowlist and the wake. */
export const BrowserFetchRequestPayloadSchema = z.object({
  requestId: z.string().min(1),
  /** Absent: the steps run on the tab an earlier fetch kept open (keepOpenMs), with nothing to open. */
  url: z.string().optional(),
  /** The deployment's browser.allowed_domains: the tab may show nothing else. */
  allowedDomains: z.array(z.string()).min(1),
  /** The deployment's browser.blocked_paths: the tab never shows, or acts on, a page on one (browserPathBlocked). */
  blockedPaths: z.array(z.string()).optional(),
  /** Each with its defaults filled in by the server: required true, minCount 1, maxCount = minCount. */
  capture: z.array(z.object({
    match: z.string().min(1),
    maxBytes: z.number().int().positive(),
    required: z.boolean(),
    minCount: z.number().int().positive(),
    maxCount: z.number().int().positive(),
    /** "last": an event stream whose every event is the whole answer so far keeps only its last complete event (pickLastEvent). */
    sse: z.enum(['last']).optional(),
  })),
  steps: z.array(FetchStepSchema),
  timeoutMs: z.number().int().positive(),
  /** Small JPEGs of the tab at its milestones, this many pixels wide; absent: none. settleMs: wait for one `results` frame (0: none). */
  frames: z.object({ width: z.number().int().positive(), settleMs: z.number().int().nonnegative().optional() }).optional(),
  /** Keep the tab open on its page this long after answering, for the next fetch with no url. */
  keepOpenMs: z.number().int().nonnegative().optional(),
  /** The kept tab's name: the one keepOpenMs keeps this tab under, and the one a fetch with no url takes. */
  tab: z.string().optional(),
  /** A hand-off: the tab's window in front of the person's, maximized, with the keyboard (fetch-op.ts). */
  front: z.boolean().optional(),
  /** With front: the page runs as a phone (default) or as the desktop page every other fetch sees. */
  frontDevice: z.enum(['phone', 'desktop']).optional(),
  /** On a hand-off: the crop is this element of its page (front_crop), measured when the fetch ends (front-crop.ts). */
  cropTo: z.object({ role: z.string().optional(), name: z.string().optional(), text: z.string().optional() }).optional(),
  /** A stage: the tab acts in front where people see it, in the same tab as the last stage fetch (fetch-op.ts). */
  stage: z.boolean().optional(),
  /** Its tab is never in the browser watch, as long as the tab lives (a person's own account page). */
  private: z.boolean().optional(),
  /** Once the required captures are in, how long to wait for every capture to fill to its maxCount (0: none). */
  graceMs: z.number().int().nonnegative().optional(),
  /** The caller's label for this fetch: the browser watch names the fetch's tab with it (browser/watch.ts). */
  tag: z.string().max(64).optional(),
});
export type BrowserFetchRequestPayload = z.infer<typeof BrowserFetchRequestPayloadSchema>;

/**
 * Why a fetch ended without every capture: a person is using the browser (yield), the page left
 * the allowed sites (or a page_fetch called a site off them), the page reached a blocked path (a
 * payment page), the time ran out (the captures so far ride along), the box browser is not there to
 * use, the page itself could not be opened, or a fetch with no url found no tab kept open for it.
 */
export const BROWSER_FETCH_FAILURES = ['browser_in_use', 'domain_not_allowed', 'page_blocked', 'timeout', 'browser_unavailable', 'navigation_failed', 'no_open_tab'] as const;
export type BrowserFetchFailure = (typeof BROWSER_FETCH_FAILURES)[number];

export const BrowserFetchCapturedSchema = z.object({
  match: z.string(),
  url: z.string(),
  status: z.number().int(),
  contentType: z.string().nullable(),
  body: z.string(),
  truncated: z.boolean(),
  atMs: z.number().int(),
});
export type BrowserFetchCaptured = z.infer<typeof BrowserFetchCapturedSchema>;

export const BrowserFetchFrameSchema = z.object({
  label: z.string(),
  atMs: z.number().int(),
  pageUrl: z.string(),
  jpegBase64: z.string(),
});
export type BrowserFetchFrame = z.infer<typeof BrowserFetchFrameSchema>;

/** Runner → server (BROWSER_FETCH_RESULT): apps/sandbox handlers/browser-fetch.ts; every path answers. */
export const BrowserFetchResultPayloadSchema = z.object({
  requestId: z.string().min(1),
  ok: z.boolean(),
  error: z.enum(BROWSER_FETCH_FAILURES).optional(),
  message: z.string().optional(),
  finalUrl: z.string().nullable(),
  elapsedMs: z.number().int(),
  /** In ms after the runner took the request: the navigation committed, the first kept response finished, the fetch ended. */
  timings: z.object({ navMs: z.number().int().nullable(), firstCaptureMs: z.number().int().nullable(), doneMs: z.number().int() }),
  captures: z.array(BrowserFetchCapturedSchema),
  /** Only when the request asked for frames. */
  frames: z.array(BrowserFetchFrameSchema).optional(),
  /** On a timeout: where it stopped (`navigate`, `2 scroll`, `captures`). */
  step: z.string().optional(),
  /** Where an open hand-off's (front) page sits on the screen, or null once none is open; absent when none is in play. */
  frontCrop: z.object({ x: z.number().int(), y: z.number().int(), width: z.number().int(), height: z.number().int() }).nullable().optional(),
  /** The hand-off asked for an element's crop (cropTo) that is not on its page yet: frontCrop is the page's, until it is. */
  frontCropPending: z.literal(true).optional(),
  /** The page called Cloudflare's Turnstile: a check on a page that answered 200. */
  challenge: z.literal('turnstile').optional(),
});
export type BrowserFetchResultPayload = z.infer<typeof BrowserFetchResultPayloadSchema>;

/**
 * Browser cookies over the relay (GET /v1/users/{user_id}/browser/cookies): which cookies of some names the box browser
 * holds for one allowed site, and until when. Names, sites and expiry only: the runner drops the values CDP hands it.
 */
/** Server → runner (BROWSER_COOKIES_REQUEST): apps/server platform/compute/browser-cookies.ts, after the allowlist. */
export const BrowserCookiesRequestPayloadSchema = z.object({
  requestId: z.string().min(1),
  /** The site, a bare host name; its subdomains' cookies count too. */
  domain: z.string().min(1),
  names: z.array(z.string().min(1)).min(1).max(10),
  /** The deployment's browser.allowed_domains: a cookie of any other site is never answered. */
  allowedDomains: z.array(z.string()),
});
export type BrowserCookiesRequestPayload = z.infer<typeof BrowserCookiesRequestPayloadSchema>;

/** Runner → server (BROWSER_COOKIES_RESULT): apps/sandbox handlers/browser-cookies.ts; every path answers. */
export const BrowserCookiesResultPayloadSchema = z.object({
  requestId: z.string().min(1),
  ok: z.boolean(),
  error: z.enum(['domain_not_allowed', 'browser_unavailable']).optional(),
  message: z.string().optional(),
  /** Never a value. expiresAt in ms since the epoch; null for a cookie that lasts as long as the browser runs. */
  cookies: z.array(z.object({ name: z.string(), domain: z.string(), expiresAt: z.number().nullable() })),
});
export type BrowserCookiesResultPayload = z.infer<typeof BrowserCookiesResultPayloadSchema>;

/**
 * Whether a response address answers a capture's (or a wait_for's) `match`: a substring of the
 * address, or, when the match ends in `$`, the end of its path (the query aside), so a decoy that
 * extends the real path (`/v3/services/meta/105` past `/v3/services`) never answers it.
 */
export function browserFetchMatches(url: string, match: string): boolean {
  if (!match.endsWith('$')) return url.includes(match);
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    return false;
  }
  return path.endsWith(match.slice(0, -1));
}

/** true when `host` is one of `allowed` or a name under one: `ixigo.com` allows `www.ixigo.com`, never `notixigo.com`. */
export function browserDomainAllowed(host: string, allowed: readonly string[]): boolean {
  const h = host.toLowerCase().replace(/\.$/, '');
  return allowed.some((d) => h === d || h.endsWith(`.${d}`));
}

/** Said wherever a blocked page is refused: the fetch's 403 and the agent's browser tools. */
export const BROWSER_PAGE_BLOCKED_TEXT = "Payment pages are closed to this browser: the deployment's browser.blocked_paths closes this page, so nothing was opened, clicked or typed on it.";

/**
 * The one rule for a deployment's browser.blocked_paths, which the server, the browser fetch and the
 * agent's browser tools all apply: the blocked entry (`ixigo.com/payments/`) whose site is the page's,
 * or one above it as allowed_domains reads it, and whose path starts the page's, read decoded and in any
 * case. null when the page is on none. Only an http(s) page can be on one.
 */
export function browserPathBlocked(url: string, blocked: readonly string[] | undefined): string | null {
  if (!blocked || blocked.length === 0) return null;
  let page: URL;
  try {
    page = new URL(url);
  } catch {
    return null;
  }
  if (page.protocol !== 'http:' && page.protocol !== 'https:') return null;
  let path = page.pathname;
  try {
    path = decodeURIComponent(path);
  } catch {
    // A path that will not decode is read as it is.
  }
  path = path.toLowerCase();
  for (const entry of blocked) {
    const slash = entry.indexOf('/');
    if (slash <= 0) continue;
    if (browserDomainAllowed(page.hostname, [entry.slice(0, slash)]) && path.startsWith(entry.slice(slash).toLowerCase())) return entry;
  }
  return null;
}
